/**
 * Time estimate for "Upload & Process All Files".
 *
 * Measured on Test, 13 Oct 2026, over 21 real uploads (created -> last update, which is the upload
 * plus reading the document): minimum 7 s, median 10 s, maximum 16 s per document. Files go up one
 * at a time, so the remaining time is the remaining files times the median, less what the file in
 * flight has already done. Analysis (the evidence step) is a separate run and is not part of this.
 */
export const SECONDS_PER_DOCUMENT = 10

export interface UploadFileState {
  status: 'pending' | 'uploading' | 'embedding' | 'complete' | 'error'
  progress: number
}

/** 0-100 across the whole batch; a failed file counts as done so the bar still finishes. */
export function batchPercent(files: UploadFileState[]): number {
  if (files.length === 0) return 0
  const done = files.reduce((sum, f) => {
    if (f.status === 'complete' || f.status === 'error') return sum + 1
    if (f.status === 'uploading' || f.status === 'embedding') return sum + Math.min(Math.max(f.progress, 0), 99) / 100
    return sum
  }, 0)
  return Math.round((done / files.length) * 100)
}

/** Whole seconds left, rounded up to the next 5 so it never claims false precision. */
export function secondsRemaining(files: UploadFileState[]): number {
  const left = files.reduce((sum, f) => {
    if (f.status === 'complete' || f.status === 'error') return sum
    if (f.status === 'uploading' || f.status === 'embedding') {
      return sum + (1 - Math.min(Math.max(f.progress, 0), 99) / 100)
    }
    return sum + 1
  }, 0)
  return Math.ceil((left * SECONDS_PER_DOCUMENT) / 5) * 5
}

export function remainingLabel(files: UploadFileState[]): string {
  const s = secondsRemaining(files)
  if (s <= 0) return 'Finishing up…'
  if (s < 60) return `About ${s} seconds left`
  const m = Math.round(s / 60)
  return `About ${m} minute${m === 1 ? '' : 's'} left`
}
